import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Calendar, Loader2, PlusCircle, Layers, Palette, Save, AlertTriangle,
  History, Edit3,
} from "lucide-react";

/**
 * Next Target Date + 3D/2D Set-Shared tracker for a design project.
 *
 * Renders three tiles inside a single card:
 *   1. Next Target Date — auto-calculated on allocation from the project
 *      amount tier; overridable by DM/DH with a mandatory reason.
 *   2. 3D Set counter (target 5) — shareable by Interior Designer + DM.
 *   3. 2D Set counter (target 2) — same shareable rule.
 *
 * Feb 2026 · overage rule: sharing beyond the target auto-flags each extra
 * set as an overage and pings the assigned Design Manager.
 */
export default function SlaAndSetsPanel({ projectId, onChanged }) {
  const [target, setTarget] = useState(null);
  const [sets, setSets] = useState(null);
  const [overriding, setOverriding] = useState(false);
  const [targetDraft, setTargetDraft] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [showAudit, setShowAudit] = useState(false);

  const load = useCallback(async () => {
    try {
      const [t, sh] = await Promise.all([
        api.get(`/design/projects/${projectId}/next-target-date`),
        api.get(`/design/projects/${projectId}/sets`),
      ]);
      setTarget(t.data);
      setSets(sh.data);
      if (t.data?.next_target_date) {
        setTargetDraft(t.data.next_target_date.slice(0, 10));
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const saveOverride = async () => {
    if (!targetDraft) { toast.error("Pick a target date"); return; }
    if (reason.trim().length < 3) { toast.error("Reason is required (min 3 chars)"); return; }
    setBusy(true);
    try {
      const iso = new Date(`${targetDraft}T18:00:00`).toISOString();
      await api.post(`/design/projects/${projectId}/next-target-date/override`, {
        next_target_date: iso,
        reason: reason.trim(),
      });
      toast.success("Next Target Date overridden");
      setOverriding(false);
      setReason("");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to override");
    } finally { setBusy(false); }
  };

  const shareSet = async (setType) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/design/projects/${projectId}/sets`, { set_type: setType });
      if (data.is_overage) {
        toast.warning(`Overage · ${setType.toUpperCase()} set #${data.entry.set_number} flagged for billing`);
      } else {
        toast.success(`${setType.toUpperCase()} set #${data.entry.set_number} logged`);
      }
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to log set");
    } finally { setBusy(false); }
  };

  if (!target || !sets) {
    return (
      <div className="iz-card p-5 flex items-center gap-2 text-xs text-gray-500">
        <Loader2 className="w-4 h-4 animate-spin"/> Loading tracker…
      </div>
    );
  }

  const targetDate = target.next_target_date ? new Date(target.next_target_date) : null;
  const daysLeft = targetDate
    ? Math.floor((targetDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;
  const isOverrideActive = target.audit?.some((a) => a.kind === "override");

  return (
    <div className="iz-card p-5 space-y-4" data-testid="sla-sets-panel">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">
          Design Delivery Tracker
        </p>
        <div className="flex items-center gap-2">
          {target.audit?.length > 0 && (
            <button onClick={() => setShowAudit((s) => !s)}
                    className="text-[10px] font-mono uppercase tracking-widest text-gray-500 hover:text-white flex items-center gap-1"
                    data-testid="audit-toggle">
              <History className="w-3 h-3"/> Audit ({target.audit.length})
            </button>
          )}
          {sets.overage_flags > 0 && (
            <span className="text-[10px] font-mono uppercase tracking-widest text-red-400 border border-red-500/40 rounded px-1.5 py-0.5 flex items-center gap-1"
                  data-testid="overage-count">
              <AlertTriangle className="w-3 h-3"/> {sets.overage_flags} overage
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Next Target Date tile */}
        <div className="border border-[#1a1a1a] rounded-md p-3 md:col-span-1" data-testid="next-target-tile">
          <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5"/> Next Target Date
          </p>
          {overriding ? (
            <div className="mt-2 space-y-2">
              <input type="date" value={targetDraft}
                     onChange={(e) => setTargetDraft(e.target.value)}
                     className="iz-input w-full text-xs"
                     data-testid="target-input"/>
              <textarea value={reason} onChange={(e) => setReason(e.target.value)}
                        rows={2}
                        placeholder="Reason for override (required)"
                        className="iz-input w-full text-xs"
                        data-testid="target-reason-input"/>
              <div className="flex gap-1">
                <button onClick={saveOverride} disabled={busy}
                        className="iz-btn-primary text-xs flex items-center gap-1"
                        data-testid="target-save">
                  <Save className="w-3 h-3"/> Override
                </button>
                <button onClick={() => { setOverriding(false); setReason(""); }}
                        className="iz-btn-secondary text-xs"
                        data-testid="target-cancel">
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <p className="text-lg font-display text-white mt-1" data-testid="target-value">
                {targetDate
                  ? targetDate.toLocaleDateString("en-IN",
                      { day: "2-digit", month: "short", year: "numeric" })
                  : "—"}
              </p>
              {daysLeft !== null && (
                <p className={`text-[11px] mt-0.5 ${
                  daysLeft < 0
                    ? "text-red-400"
                    : daysLeft <= 3 ? "text-[#d4af37]" : "text-gray-500"
                }`} data-testid="target-days-left">
                  {daysLeft < 0
                    ? `${Math.abs(daysLeft)} d overdue`
                    : `${daysLeft} d remaining`}
                </p>
              )}
              <p className="text-[10px] text-gray-600 mt-1">
                Stage: <span className="text-gray-400">{target.next_target_stage}</span>
                {target.next_target_tier && (
                  <span className="ml-2">· Tier: <span className="text-gray-400">{target.next_target_tier}</span></span>
                )}
                {isOverrideActive && (
                  <span className="ml-2 text-[#d4af37]">· Manual</span>
                )}
              </p>
              {target.editable && (
                <button onClick={() => setOverriding(true)}
                        className="text-[10px] text-[#d4af37] hover:underline mt-2 flex items-center gap-1"
                        data-testid="target-override-btn">
                  <Edit3 className="w-3 h-3"/> Override
                </button>
              )}
            </>
          )}
        </div>

        <SetCounter testid="counter-3d" label="3D Sets Shared" icon={Palette}
                    shared={sets.sets_3d_shared} target={sets.sets_3d_target}
                    canShare={sets.can_share} busy={busy}
                    onShare={() => shareSet("3d")}/>
        <SetCounter testid="counter-2d" label="2D Sets Shared" icon={Layers}
                    shared={sets.sets_2d_shared} target={sets.sets_2d_target}
                    canShare={sets.can_share} busy={busy}
                    onShare={() => shareSet("2d")}/>
      </div>

      {/* Audit log */}
      {showAudit && target.audit?.length > 0 && (
        <div className="pt-3 border-t border-[#1a1a1a]">
          <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-2">
            Target Date Audit Log
          </p>
          <ul className="space-y-2 max-h-56 overflow-y-auto" data-testid="target-audit-log">
            {[...target.audit].reverse().map((a) => (
              <li key={a.id} className="text-[11px] border border-[#161616] rounded px-2 py-1.5">
                <p className="text-white">
                  <span className={`font-mono uppercase tracking-widest text-[9px] px-1.5 py-0.5 rounded border mr-2 ${
                    a.kind === "override"
                      ? "border-[#d4af37]/50 text-[#d4af37]"
                      : "border-[#262626] text-gray-500"
                  }`}>
                    {a.kind.replace(/_/g, " ")}
                  </span>
                  {a.from ? new Date(a.from).toLocaleDateString() : "—"}
                  {" → "}
                  {a.to ? new Date(a.to).toLocaleDateString() : "—"}
                </p>
                <p className="text-gray-500 mt-0.5">
                  {a.reason} · {a.by_user_name || "system"} · {new Date(a.at).toLocaleString()}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {sets.sets_log.length > 0 && (
        <div className="pt-3 border-t border-[#1a1a1a]">
          <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-2">
            Set-Shared Activity ({sets.sets_log.length})
          </p>
          <ul className="space-y-1 max-h-40 overflow-y-auto" data-testid="sets-log-list">
            {[...sets.sets_log].reverse().slice(0, 6).map((e) => (
              <li key={e.id}
                  className={`text-[11px] flex items-center gap-2 px-2 py-1 rounded ${
                    e.is_overage ? "bg-red-500/5 text-red-300" : "text-gray-400"
                  }`}
                  data-testid={`set-entry-${e.id}`}>
                <span className={`font-mono uppercase tracking-widest text-[9px] px-1.5 py-0.5 rounded border ${
                  e.is_overage
                    ? "border-red-500/60 text-red-300"
                    : "border-[#262626] text-gray-500"
                }`}>
                  {e.set_type} · #{e.set_number}
                  {e.is_overage ? " · overage" : ""}
                </span>
                <span className="text-gray-300 truncate">{e.by_user_name || "—"}</span>
                <span className="text-gray-600 ml-auto">
                  {new Date(e.at).toLocaleDateString()}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function SetCounter({ testid, label, icon: Icon, shared, target, canShare, busy, onShare }) {
  const ratio = Math.min(shared / target, 1);
  const overage = shared > target;
  const barColor = overage
    ? "bg-red-500"
    : ratio >= 0.8
      ? "bg-[#d4af37]"
      : "bg-emerald-500";
  return (
    <div className="border border-[#1a1a1a] rounded-md p-3" data-testid={testid}>
      <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 flex items-center gap-1">
        <Icon className="w-3.5 h-3.5"/> {label}
      </p>
      <p className="text-lg font-display text-white mt-1" data-testid={`${testid}-value`}>
        {shared}
        <span className="text-gray-500 text-sm">/{target}</span>
        {overage && (
          <span className="ml-2 text-[10px] font-mono uppercase tracking-widest text-red-400">
            +{shared - target} overage
          </span>
        )}
      </p>
      <div className="mt-2 h-1.5 bg-[#1a1a1a] rounded overflow-hidden">
        <div className={`h-full ${barColor} transition-all`}
             style={{ width: `${ratio * 100}%` }}/>
      </div>
      {canShare && (
        <button onClick={onShare} disabled={busy}
                className="iz-btn-primary text-xs flex items-center gap-1 mt-3 w-full justify-center"
                data-testid={`${testid}-share-btn`}>
          <PlusCircle className="w-3.5 h-3.5"/> Set Shared
        </button>
      )}
    </div>
  );
}
